import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  SupportMailRow,
  useCreateCaseFromEmailMutation,
  useGetSupportEmailQuery,
  useGetSupportEmailsQuery,
  useRedraftSupportTicketMutation,
} from "../../slices/SupportSlice";
import { HelpDeskHeader } from "./HelpDeskParts";
import {
  addressLabel,
  caseButton,
  createdMessage,
  emailFrameDocument,
  mailDate,
  mailDateLong,
  MailFolder,
  mailWho,
  readFolder,
} from "./mailbox.utils";
import { errorText, PILL_CLASSES } from "./support.utils";
import { PAGE, PAGE_INNER, PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";
import { WriteWithInstructions } from "./WriteWithInstructions";

/**
 * Help desk › Emails (owner 2026-10-26): every email in support@'s Inbox and Sent, of any date (the Help desk only
 * turns NEW inbox emails into cases), opened like the Mail page. "Create case" turns an email's conversation into a
 * case without sending anything and without an AI draft.
 */
export const EmailsPage = () => {
  const [params, setParams] = useSearchParams();
  const folder = readFolder(params.get("folder"));
  const q = params.get("q") || "";
  const selected = params.get("email");
  const [search, setSearch] = useState(q);
  const [tokens, setTokens] = useState<(string | null)[]>([null]);

  useEffect(() => setTokens([null]), [folder, q]);
  useEffect(() => setSearch(q), [q]);

  const change = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    setParams(p, { replace: true });
  };

  const first = useGetSupportEmailsQuery({ folder, q, pageToken: null });
  const connected = first.data?.connected;

  return (
    <div className={PAGE}>
      <div className={PAGE_INNER}>
        <HelpDeskHeader
          section={"emails"}
          subtitle={
            "Everything in support@, old and new. Open an email and click “Create case” to work on it here: nothing is sent and no draft is written until you ask."
          }
        />
        {first.data && !connected ? (
          <div
            className={
              "rounded-[14px] border border-[#F5DEB0] bg-[#FFF9EE] px-4 py-3 text-[13.5px] text-[#7A5A12]"
            }
            data-testid={"emails-not-connected"}>
            <b>support@ isn&apos;t connected.</b> Connect it on the{" "}
            <Link to={"/support"} className={"font-semibold text-[#6B4EFF]"}>
              Support cases
            </Link>{" "}
            page to see its emails here.
          </div>
        ) : (
          <div
            className={
              "grid min-h-[560px] grid-cols-1 overflow-hidden rounded-[14px] border border-[#EDEFF3] bg-white lg:grid-cols-[minmax(320px,420px)_1fr]"
            }>
            <div
              className={
                "flex min-w-0 flex-col border-b border-[#EDEFF3] lg:border-b-0 lg:border-r"
              }>
              <div
                className={
                  "flex flex-col gap-2.5 border-b border-[#EDEFF3] p-3"
                }>
                <div
                  role={"tablist"}
                  aria-label={"Folder"}
                  className={
                    "inline-flex w-fit gap-1 rounded-lg bg-[#ECEEF3] p-1"
                  }>
                  {(["inbox", "sent"] as MailFolder[]).map((f) => (
                    <button
                      key={f}
                      type={"button"}
                      role={"tab"}
                      aria-selected={folder === f}
                      onClick={() =>
                        change({
                          folder: f === "inbox" ? null : f,
                          email: null,
                        })
                      }
                      className={classNames(
                        "h-8 rounded-md px-4 text-[13px]",
                        folder === f
                          ? "bg-white font-bold text-[#1F2433] shadow-[0_1px_2px_rgba(20,20,40,.08)]"
                          : "font-semibold text-[#3A4256]",
                      )}>
                      {f === "inbox" ? "Inbox" : "Sent"}
                    </button>
                  ))}
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    change({ q: search.trim() || null, email: null });
                  }}
                  className={"flex gap-2"}>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={"Search a name, email address or subject"}
                    aria-label={"Search emails"}
                    className={
                      "h-9 min-w-0 flex-1 rounded-lg border border-[#DCE2EA] px-3 text-[13.5px] outline-none focus:border-[#6B4EFF]"
                    }
                  />
                  <button type={"submit"} className={SMALL_BUTTON}>
                    Search
                  </button>
                  {q && (
                    <button
                      type={"button"}
                      className={SMALL_BUTTON}
                      onClick={() => change({ q: null, email: null })}>
                      Clear
                    </button>
                  )}
                </form>
              </div>
              <div
                className={"max-h-[70vh] min-h-0 flex-1 overflow-y-auto"}
                data-testid={"emails-list"}>
                {tokens.map((token, i) => (
                  <MailPage
                    key={`${folder}|${q}|${token || ""}`}
                    folder={folder}
                    q={q}
                    token={token}
                    selected={selected}
                    onSelect={(id) => change({ email: id })}
                    onMore={
                      i === tokens.length - 1
                        ? (next) => setTokens((t) => [...t, next])
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
            <div className={"min-w-0"}>
              {selected ? (
                <MailReaderPane
                  id={selected}
                  onClose={() => change({ email: null })}
                />
              ) : (
                <div
                  className={
                    "flex h-full min-h-[300px] items-center justify-center p-8 text-center text-[14px] text-[#8A90A0]"
                  }>
                  Pick an email to read it.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MailPage = ({
  folder,
  q,
  token,
  selected,
  onSelect,
  onMore,
}: {
  folder: MailFolder;
  q: string;
  token: string | null;
  selected: string | null;
  onSelect: (id: string) => void;
  onMore?: (next: string) => void;
}) => {
  const { data, isLoading, isFetching, error } = useGetSupportEmailsQuery({
    folder,
    q,
    pageToken: token,
  });
  if (isLoading)
    return (
      <div className={"flex justify-center py-8"}>
        <LottieLoading />
      </div>
    );
  if (error)
    return (
      <div className={"p-4 text-[13.5px] text-[#B42318]"} role={"alert"}>
        {errorText(error, "Couldn't load the emails. Please try again.")}
      </div>
    );
  const rows = data?.emails || [];
  return (
    <>
      {token === null && rows.length === 0 && (
        <div className={"p-6 text-center text-[13.5px] text-[#8A90A0]"}>
          {q ? "No emails match your search." : "No emails here."}
        </div>
      )}
      {rows.map((row) => (
        <MailRow
          key={row.id}
          row={row}
          folder={folder}
          active={row.id === selected}
          onClick={() => onSelect(row.id)}
        />
      ))}
      {onMore && data?.nextPageToken && (
        <div className={"p-3 text-center"}>
          <button
            type={"button"}
            className={SMALL_BUTTON}
            disabled={isFetching}
            onClick={() => onMore(data.nextPageToken as string)}>
            Show older emails
          </button>
        </div>
      )}
    </>
  );
};

const MailRow = ({
  row,
  folder,
  active,
  onClick,
}: {
  row: SupportMailRow;
  folder: MailFolder;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type={"button"}
    onClick={onClick}
    data-testid={"email-row"}
    aria-current={active ? "true" : undefined}
    className={classNames(
      "block w-full border-b border-[#F1F2F5] px-4 py-3 text-left",
      active ? "bg-[#F3F0FF]" : "hover:bg-[#F8F9FB]",
    )}>
    <div className={"flex items-baseline gap-2"}>
      {row.unread && (
        <span
          className={"h-2 w-2 shrink-0 rounded-full bg-[#6B4EFF]"}
          aria-label={"Unread"}
        />
      )}
      <span
        className={classNames(
          "min-w-0 flex-1 truncate text-[13.5px] text-[#1F2433]",
          row.unread ? "font-bold" : "font-semibold",
        )}>
        {mailWho(row, folder)}
      </span>
      <span className={"shrink-0 text-[12px] text-[#8A90A0]"}>
        {mailDate(row.date)}
      </span>
    </div>
    <div className={"mt-0.5 flex items-center gap-2"}>
      <span className={"min-w-0 flex-1 truncate text-[13px] text-[#1F2433]"}>
        {row.subject || "(no subject)"}
      </span>
      {row.hasAttachments && (
        <span
          className={"shrink-0 text-[12px] text-[#8A90A0]"}
          title={"Has attachments"}>
          📎
        </span>
      )}
      {row.caseId && (
        <span
          className={classNames(
            "shrink-0 rounded-full px-2 py-[1px] text-[11px] font-semibold",
            PILL_CLASSES.purple,
          )}>
          Case #{row.caseCode}
        </span>
      )}
    </div>
    <div className={"mt-0.5 truncate text-[12.5px] text-[#8A90A0]"}>
      {row.snippet}
    </div>
  </button>
);

const MailReaderPane = ({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { data, isFetching, error } = useGetSupportEmailQuery(id);
  const [createCase, { isLoading: creating }] =
    useCreateCaseFromEmailMutation();
  const [redraft, { isLoading: drafting }] = useRedraftSupportTicketMutation();
  const [instructing, setInstructing] = useState(false);
  useEffect(() => setInstructing(false), [id]);
  if (error)
    return (
      <div className={"p-6 text-[13.5px] text-[#B42318]"} role={"alert"}>
        {errorText(error, "Couldn't open this email.")}
      </div>
    );
  if (!data || (isFetching && data.id !== id))
    return (
      <div className={"flex justify-center py-12"}>
        <LottieLoading />
      </div>
    );
  const button = caseButton(data);
  const onCase = async () => {
    if (button.existing && data.caseId) {
      navigate(`/support/${data.caseId}`);
      return;
    }
    try {
      const r = await createCase(data.id).unwrap();
      toast.success(createdMessage(r));
      navigate(`/support/${r.ticketId}`);
    } catch (e) {
      toast.error(errorText(e, "Couldn't create the case. Please try again."));
    }
  };
  // "Reply with instructions" (owner 2026-10-27): the email's case (made if needed, nothing sent), then a draft
  // written from the owner's instructions, opened on the case page to check and send.
  const onInstructed = async (instructions: string) => {
    try {
      const ticketId =
        data.caseId || (await createCase(data.id).unwrap()).ticketId;
      const updated = await redraft({ id: ticketId, instructions }).unwrap();
      toast.success(
        updated.draft
          ? `Draft ready in case #${updated.code}. Check it and click Send.`
          : `Case #${updated.code} is open. The AI couldn't write that one; please write it yourself.`,
      );
      navigate(`/support/${ticketId}`);
    } catch (e) {
      toast.error(
        errorText(e, "The AI couldn't write a draft. Please try again."),
      );
    }
  };
  const list = (
    label: string,
    items: { email: string; name?: string | null }[],
  ) =>
    items.length > 0 && (
      <div className={"text-[13px] text-[#5D6475]"}>
        <span className={"font-semibold text-[#3A4256]"}>{label}</span>{" "}
        {items
          .map((a) => (a.name ? `${a.name} <${a.email}>` : a.email))
          .join(", ")}
      </div>
    );
  return (
    <div className={"flex h-full flex-col"} data-testid={"email-reader"}>
      <div
        className={
          "flex flex-wrap items-start gap-3 border-b border-[#EDEFF3] px-5 py-4"
        }>
        <div className={"min-w-0 flex-1"}>
          <h2 className={"text-[18px] font-extrabold text-[#1F2433]"}>
            {data.subject || "(no subject)"}
          </h2>
          <div className={"mt-1.5 text-[13px] text-[#5D6475]"}>
            <span className={"font-semibold text-[#3A4256]"}>From</span>{" "}
            {data.from
              ? `${addressLabel(data.from)}${
                  data.from.name ? ` <${data.from.email}>` : ""
                }`
              : "—"}
            {" · "}
            {mailDateLong(data.date)}
          </div>
          {list("To", data.to)}
          {list("Cc", data.cc)}
          {data.replyTo &&
            data.replyTo.email !== data.from?.email &&
            list("Reply-To", [data.replyTo])}
        </div>
        <div className={"flex shrink-0 gap-2"}>
          <button
            type={"button"}
            className={SMALL_BUTTON + " lg:hidden"}
            onClick={onClose}>
            Back
          </button>
          {!instructing && (
            <button
              type={"button"}
              className={SMALL_BUTTON}
              onClick={() => setInstructing(true)}
              disabled={creating || drafting}
              data-testid={"email-reply-instructions"}>
              ✍️ Reply with instructions
            </button>
          )}
          <button
            type={"button"}
            className={button.existing ? SMALL_BUTTON : PRIMARY_BUTTON}
            onClick={onCase}
            disabled={creating}
            data-testid={"email-case-button"}>
            {creating ? "Creating…" : button.label}
          </button>
        </div>
      </div>
      {instructing && (
        <div className={"border-b border-[#EDEFF3] px-5 pt-3"}>
          <WriteWithInstructions
            busy={creating || drafting}
            onWrite={(instructions) => void onInstructed(instructions)}
            onCancel={() => setInstructing(false)}
            note={
              button.existing
                ? `The draft goes into case #${data.caseCode}, where you check it and click Send.`
                : "This makes a case from the conversation (no automatic reply) and puts the draft there for you to check and send."
            }
          />
        </div>
      )}
      {!button.existing && (
        <div
          className={
            "border-b border-[#EDEFF3] bg-[#F8F9FB] px-5 py-2 text-[12.5px] text-[#5D6475]"
          }>
          Creating a case brings in the whole conversation. Nothing is sent to
          the customer, and no draft is written until you ask for one.
        </div>
      )}
      {data.attachmentNames.length > 0 && (
        <div
          className={
            "flex flex-wrap gap-2 border-b border-[#EDEFF3] px-5 py-2.5"
          }>
          {data.attachmentNames.map((n, i) => (
            <span
              key={`${n}-${i}`}
              className={
                "rounded-md border border-[#DCE2EA] bg-white px-2 py-1 text-[12.5px] text-[#3A4256]"
              }>
              📎 {n}
            </span>
          ))}
        </div>
      )}
      <EmailBody html={data.bodyHtml || data.snippet} />
    </div>
  );
};

/** The email in a sandboxed frame: no scripts run, and its own styles stay inside it. */
const EmailBody = ({ html }: { html: string }) => {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(300);
  const fit = () => {
    const doc = frame.current?.contentDocument;
    if (doc?.body)
      setHeight(Math.min(Math.max(doc.body.scrollHeight + 24, 160), 4000));
  };
  return (
    <div className={"px-5 py-4"}>
      <iframe
        ref={frame}
        title={"Email"}
        sandbox={
          "allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        }
        srcDoc={emailFrameDocument(html)}
        onLoad={fit}
        style={{ height }}
        className={"w-full border-0"}
      />
    </div>
  );
};
